import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useNotifStore } from '@/stores/notifications';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';

interface SearchResult {
  people?: Array<{ id: string; display_name: string; username: string; avatar_key: string | null }>;
  groups?: Array<{ id: string; name: string; member_count: number }>;
  notes?: Array<{ id: string; title: string; author_display_name: string }>;
}

export function Header() {
  const { user, logout } = useAuthStore();
  const { unreadCount } = useNotifStore();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSearch(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (!user) return null;

  const navItems = [
    { to: '/feed', label: 'Feed', icon: '\ud83d\udcf0' },
    { to: '/messages', label: 'Messages', icon: '\ud83d\udcac' },
    { to: '/events', label: 'Events', icon: '\ud83d\udcc5' },
    { to: '/groups', label: 'Groups', icon: '\ud83c\udfe0\ufe0f' },
    { to: '/notes', label: 'Notes', icon: '\ud83d\udcdd' },
    { to: '/directory', label: 'Directory', icon: '\ud83c\udfea' },
    { to: '/friends', label: 'Friends', icon: '\ud83d\udc65' },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSearchResults(null);
      setShowSearch(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await api.get<SearchResult>(`/search?q=${encodeURIComponent(val.trim())}`);
        setSearchResults(results);
        setShowSearch(true);
      } catch {
        setSearchResults(null);
      }
    }, 300);
  };

  const closeSearch = () => {
    setShowSearch(false);
  };

  const hasPeople = searchResults?.people && searchResults.people.length > 0;
  const hasGroups = searchResults?.groups && searchResults.groups.length > 0;
  const hasNotes = searchResults?.notes && searchResults.notes.length > 0;
  const hasAnyResults = hasPeople || hasGroups || hasNotes;

  return (
    <header className="bg-gradient-to-b from-vibe-400 to-vibe-500 sticky top-0 z-50 border-b border-vibe-700">
      <div className="max-w-5xl mx-auto px-3 py-1.5 flex items-center justify-between gap-2">
        {/* Logo */}
        <Link to="/feed" className="flex items-center gap-2 shrink-0">
          <div className="w-7 h-7 bg-white/95 rounded-md flex items-center justify-center shadow-sm">
            <span className="text-base">{'\u223f'}</span>
          </div>
          <span className="text-white font-bold text-lg tracking-tight hidden sm:inline"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)', letterSpacing: '0.02em' }}>
            vibration
          </span>
        </Link>

        {/* Search bar */}
        <div className="relative flex-1 min-w-[120px] max-w-xs" ref={searchRef}>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sand-400 text-xs">{'\ud83d\udd0d'}</span>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchResults) setShowSearch(true); }}
              placeholder="Search..."
              className="w-full pl-7 pr-2 py-1 text-xs rounded bg-white/90 text-sand-800 placeholder-sand-400 border-0 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>
          {showSearch && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-sand-300 max-h-80 overflow-y-auto z-50">
              {!hasAnyResults && (
                <div className="p-3 text-xs text-sand-500 text-center">No results found</div>
              )}
              {hasPeople && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-bold text-sand-500 uppercase bg-sand-50">People</div>
                  {searchResults.people!.map((p) => (
                    <Link
                      key={p.id}
                      to={`/profile/${p.username}`}
                      onClick={closeSearch}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-sand-50 transition-colors"
                    >
                      <img src={mediaUrl(p.avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-sand-800 truncate">{p.display_name}</div>
                        <div className="text-[10px] text-sand-500">@{p.username}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {hasGroups && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-bold text-sand-500 uppercase bg-sand-50">Groups</div>
                  {searchResults.groups!.map((g) => (
                    <Link
                      key={g.id}
                      to={`/groups/${g.id}`}
                      onClick={closeSearch}
                      className="block px-3 py-1.5 hover:bg-sand-50 transition-colors"
                    >
                      <div className="text-xs font-bold text-sand-800">{g.name}</div>
                      <div className="text-[10px] text-sand-500">{g.member_count} members</div>
                    </Link>
                  ))}
                </div>
              )}
              {hasNotes && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-bold text-sand-500 uppercase bg-sand-50">Notes</div>
                  {searchResults.notes!.map((n) => (
                    <Link
                      key={n.id}
                      to={`/notes/${n.id}`}
                      onClick={closeSearch}
                      className="block px-3 py-1.5 hover:bg-sand-50 transition-colors"
                    >
                      <div className="text-xs font-bold text-sand-800">{n.title}</div>
                      <div className="text-[10px] text-sand-500">by {n.author_display_name}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav — hidden on mobile, shown on md+ */}
        <nav className="hidden md:flex items-center gap-0.5">
          {navItems.map(({ to, label, icon }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                  active
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="sm:hidden text-sm">{icon}</span>
                <span className="hidden sm:inline">{label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Notifications */}
          <Link
            to="/notifications"
            className="relative text-white/70 hover:text-white transition-colors px-1.5 py-1 rounded hover:bg-white/10"
          >
            <span className="text-sm">{'\ud83d\udd14'}</span>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-400 text-white text-[9px] font-bold rounded-full px-1 min-w-[14px] text-center leading-[14px]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>

          {/* Profile */}
          <Link to={`/profile/${user.username}`} className="flex items-center gap-1.5 hover:bg-white/10 rounded px-1.5 py-1 transition-colors">
            <img
              src={mediaUrl(user.avatar_key)}
              alt=""
              className="w-6 h-6 rounded object-cover border border-white/30"
            />
            <span className="text-white text-xs font-bold hidden md:inline">{user.display_name || user.username}</span>
          </Link>

          {/* Sign out */}
          <button
            onClick={logout}
            className="text-white/50 hover:text-white text-xs px-1.5 py-1 hover:bg-white/10 rounded transition-colors"
          >
            Sign&nbsp;out
          </button>
        </div>
      </div>
    </header>
  );
}
